import { useState, useEffect } from 'react';
import { useClients } from '../../hooks/useClients';
import { syncNewClients, getClientCheckTimestamps, ClientCheckTimestamp } from '../../services/api';
import ClientsTable from './ClientsTable';
import ClientForm from './ClientForm';
import HelpTooltip from '../common/HelpTooltip';
import { Search, Plus, RefreshCw, UserPlus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function ClientsPage() {
  const navigate = useNavigate();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [checkTimestamps, setCheckTimestamps] = useState<Record<string, ClientCheckTimestamp>>({});

  const {
    clients,
    loading,
    error,
    filters,
    setFilters,
    refetch,
  } = useClients({ includeSubscriptions: true, pageSize: 500 });

  // Fetch check timestamps in parallel with clients
  useEffect(() => {
    getClientCheckTimestamps()
      .then(setCheckTimestamps)
      .catch(() => {}); // Silently fail - timestamps are supplementary
  }, []);

  // Debounced live search - triggers search 300ms after user stops typing
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (searchTerm !== (filters.search || '')) {
        setFilters({ ...filters, search: searchTerm || undefined, page: 1 });
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setFilters({ ...filters, search: searchTerm, page: 1 });
  };

  const handleSyncNewClients = async () => {
    setSyncing(true);
    setSyncMessage(null);
    try {
      const result = await syncNewClients();
      if (result.newClientsAdded > 0) {
        setSyncMessage({
          type: 'success',
          text: `Added ${result.newClientsAdded} new client(s): ${result.newClientNames.slice(0, 3).join(', ')}${result.newClientNames.length > 3 ? '...' : ''}`
        });
        refetch();
      } else {
        setSyncMessage({
          type: 'success',
          text: `No new clients found. All ${result.totalPax8Companies} Pax8 clients are already synced.`
        });
      }
    } catch (err) {
      setSyncMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to sync new clients'
      });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-foreground">
              Clients
            </h1>
            <HelpTooltip
              text="View and manage all your Pax8 clients. Each client can have multiple subscriptions. Click on a client to see their details, subscriptions, users, and groups."

            />
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage your client relationships and payment status
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center space-x-2">
          <button
            onClick={handleSyncNewClients}
            disabled={syncing}
            className="btn-secondary"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync New Clients'}
          </button>
          <HelpTooltip
            text="Checks Pax8 for new clients that haven't been added to this portal yet. Use this after adding new clients in Pax8."
            size="sm"
          />
          <button
            onClick={() => navigate('/clients/onboard')}
            className="btn-primary"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Onboard Client
          </button>
          <HelpTooltip
            text="Onboard a new client with full GDAP setup - creates admin relationship, assigns roles, and grants app consent."
            size="sm"
          />
          <button
            onClick={() => setShowCreateForm(true)}
            className="btn-secondary"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Manually
          </button>
          <HelpTooltip
            text="Manually add a client that isn't in Pax8, or create a placeholder before the Pax8 sync."
            size="sm"
          />
        </div>
      </div>

      {/* Sync Status Message */}
      {syncMessage && (
        <div className={`card p-4 ${
          syncMessage.type === 'success'
            ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300'
            : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300'
        }`}>
          {syncMessage.text}
        </div>
      )}

      {/* Search */}
      <div className="card p-4">
        <form onSubmit={handleSearch}>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search clients..."
              className="input pl-10"
            />
          </div>
        </form>
      </div>

      {/* Error State */}
      {error && (
        <div className="card p-4 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Table */}
      <ClientsTable
        clients={clients}
        loading={loading}
        checkTimestamps={checkTimestamps}
      />

      {/* Create Client Modal */}
      {showCreateForm && (
        <ClientForm
          onClose={() => setShowCreateForm(false)}
          onSuccess={() => {
            setShowCreateForm(false);
            refetch();
          }}
        />
      )}
    </div>
  );
}
